import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormError } from '@wix/form-viewer/widget';
import { v4 as uuid } from 'uuid';
import { ParticipantStep } from '../../../../../../types/v3Types';
import { IFormHandleExtended } from '../../../components/FormQuiz';
import { ButtonNames } from '../../../../../../contexts/main/biInterfaces';
import {
  AiChatMessageSource,
  PendingAiChatMessage,
  SidebarLayoutBaseContext,
} from '../contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import { classes } from '../SidebarLayoutForParticipant.st.css';
import utils, {
  getFlatStepsList,
  IFollowingEntity,
  isSectionLockedForStep,
} from '../../utils';
import { SidebarContent } from './SidebarContent';
import { BodyContent } from './BodyContent';
import AiChatPanel from './AiChatPanel';
import AiChatDialog from './AiChatDialog';
import { Props } from '../interfaces';
import { getChatStepContext } from '../../../../../../contexts/storage-contexts/AIChat/stepContext';

export interface SidebarWithChallengeProps {
  props: Props;
  currentStep: ParticipantStep | null;
  mobileScreen: 'list' | 'view';
  formQuizRef: React.RefObject<IFormHandleExtended>;
  formValidity: FormError[];
  setFormValidity: (formValidity: FormError[]) => void;
  wixAdvertisementBannerHeight?: number;
  cssPBEnabled: boolean;
  wrapperRef: React.RefObject<HTMLDivElement>;
  stepViewRef: React.RefObject<HTMLDivElement>;
  videoStepAccepted: boolean;
  setVideoStepAccepted: (videoStepAccepted: boolean) => void;
  isVideoLoadingError: boolean;
  setIsVideoLoadingError: (isVideoLoadingError: boolean) => void;
  isAutoPlayVideo: boolean;
  changeAutoplayOptions: (isAutoplayEnabled: boolean) => Promise<void>;
  checkAutoNavigate: () => Promise<void>;
  goToCurrentStep: () => void;
  resolveCurrentStep: () => Promise<void>;
  onStepChosen: (step: ParticipantStep) => void;
  goToMobileList: () => void;
  getQuizData: () => any;
  resolveQuiz: (quizSubmissionId?: string) => Promise<void>;
  onStepResolve: (
    step: ParticipantStep,
    options?: { background?: boolean },
  ) => Promise<void>;
  onNextEntity: (
    nextEntity: IFollowingEntity,
    withBI?: boolean,
  ) => Promise<void>;
  getIsStartDisabled: () => boolean;
  setStep: (currentStep: ParticipantStep) => void;
  sendBiButtonClick: (buttonName: ButtonNames, step?: string) => void;
  isProgramFinished: boolean;
  isCollapsibleSidebarEnabled: boolean;
  isSidebarCollapsed: boolean;
  toggleSidebarCollapse: () => void;
  isAiChatVisible: boolean;
  isAiChatEnabled: boolean;
  toggleAiChatVisibility: () => void;
}

export const SidebarWithChallenge: React.FC<SidebarWithChallengeProps> = ({
  props,
  currentStep,
  formQuizRef,
  formValidity,
  setFormValidity,
  wixAdvertisementBannerHeight,
  cssPBEnabled,
  wrapperRef,
  stepViewRef,
  videoStepAccepted,
  setVideoStepAccepted,
  isVideoLoadingError,
  setIsVideoLoadingError,
  isAutoPlayVideo,
  changeAutoplayOptions,
  checkAutoNavigate,
  goToCurrentStep,
  resolveCurrentStep,
  onStepChosen,
  goToMobileList,
  getQuizData,
  resolveQuiz,
  onStepResolve,
  onNextEntity,
  getIsStartDisabled,
  setStep,
  sendBiButtonClick,
  isProgramFinished,
  isCollapsibleSidebarEnabled,
  isSidebarCollapsed,
  toggleSidebarCollapse,
  isAiChatVisible,
  isAiChatEnabled,
  toggleAiChatVisibility,
  mobileScreen,
}) => {
  const [pendingAiChatMessage, setPendingAiChatMessage] =
    useState<PendingAiChatMessage | null>(null);
  const isAiChatVisibleRef = useRef(isAiChatVisible);
  const stickyPositionTop = wixAdvertisementBannerHeight
    ? `${wixAdvertisementBannerHeight}px`
    : undefined;

  useEffect(() => {
    isAiChatVisibleRef.current = isAiChatVisible;
  }, [isAiChatVisible]);

  // A queued message belongs to the chat session it opened; drop it if the
  // chat is closed (or becomes unavailable) before it was sent
  useEffect(() => {
    if (!isAiChatVisible || !isAiChatEnabled) {
      setPendingAiChatMessage(null);
    }
  }, [isAiChatVisible, isAiChatEnabled]);

  const isCurrentStepHidden =
    !!currentStep?.id &&
    utils.isStepHidden(
      props.challengeData?.challenge,
      getFlatStepsList({
        listParticipantSections: props.listParticipantSections,
        participantSteps: props.participantSteps,
      }),
      currentStep,
    );
  // Mirrors what StepView renders: no step content for a participant in a
  // negative state, a locked section, a hidden step or the mobile list
  const isStepOnScreen =
    !props.isParticipantInSuspendedState &&
    !props.isParticipantInLockedState &&
    !isSectionLockedForStep(currentStep, props.listParticipantSections)
      .isSectionLocked &&
    !isCurrentStepHidden &&
    (!props.isMobile || mobileScreen === 'view');
  const chatStepContext = useMemo(
    () => (isStepOnScreen ? getChatStepContext(currentStep) : undefined),
    [currentStep, isStepOnScreen],
  );

  const openAiChatWithMessage = useCallback(
    (message: string, source: AiChatMessageSource) => {
      setPendingAiChatMessage(
        (currentMessage) =>
          currentMessage ?? {
            id: uuid(),
            message,
            source,
            stepContext: chatStepContext,
          },
      );

      // Only ever open the chat, never close an already open one.
      if (!isAiChatVisibleRef.current) {
        isAiChatVisibleRef.current = true;
        toggleAiChatVisibility();
      }
    },
    [chatStepContext, toggleAiChatVisibility],
  );

  const clearPendingAiChatMessage = useCallback((id: string) => {
    setPendingAiChatMessage((currentMessage) =>
      currentMessage?.id === id ? null : currentMessage,
    );
  }, []);

  return (
    <SidebarLayoutBaseContext.Provider
      value={{
        formQuizRef,
        formValidity,
        setFormValidity,
        currentStep,
        isCurrentStepHidden,
        chatStepContext,
        goToCurrentStep,
        resolveCurrentStep,
        isAutoPlayVideo,
        changeAutoplayOptions,
        checkAutoNavigate,
        pageRef: props.pageRef,
        videoStepAccepted,
        setVideoStepAccepted,
        isVideoLoadingError,
        setIsVideoLoadingError,
        isSidebarCollapsed,
        isCollapsibleSidebarEnabled,
        toggleSidebarCollapse,
        isAiChatVisible,
        isAiChatEnabled,
        toggleAiChatVisibility,
        pendingAiChatMessage,
        openAiChatWithMessage,
        clearPendingAiChatMessage,
      }}
    >
      <div className={classes.wrapper} ref={wrapperRef}>
        <div
          style={{
            top: stickyPositionTop,
          }}
          className={`${classes.column} ${classes.sidebar}`}
        >
          <SidebarContent
            props={props}
            currentStep={currentStep}
            onStepChosen={onStepChosen}
            isProgramFinished={isProgramFinished}
            getIsStartDisabled={getIsStartDisabled}
            setStep={setStep}
            sendBiButtonClick={sendBiButtonClick}
          />
        </div>
        <div
          style={{
            width: cssPBEnabled
              ? 'calc(var(--opgSidebarLayoutSpace) * 1px)'
              : `${props.settings.sidebarLayoutSpace}px`,
          }}
        />
        <div
          ref={stepViewRef}
          className={`${classes.column} ${classes.contentBody}`}
        >
          <div className={classes.bodyContentContainer}>
            <BodyContent
              props={props}
              currentStep={currentStep}
              goToMobileList={goToMobileList}
              goToCurrentStep={goToCurrentStep}
              getQuizData={getQuizData}
              resolveQuiz={resolveQuiz}
              onStepResolve={onStepResolve}
              onNextEntity={onNextEntity}
              isProgramFinished={isProgramFinished}
              formQuizRef={formQuizRef}
              getIsStartDisabled={getIsStartDisabled}
              setStep={setStep}
              sendBiButtonClick={sendBiButtonClick}
            />
          </div>
          {isAiChatVisible ? <AiChatPanel /> : null}
        </div>
        <AiChatDialog />
      </div>
    </SidebarLayoutBaseContext.Provider>
  );
};
